import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@ublend-npm/react-tooltip';
import {
  Checkbox,
  Chip,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
} from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import useStyles from './MultipleSelect.styled';

const menuProps = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  getContentAnchorEl: null,
  disableAutoFocusItem: true,
};

const CHIP_GAP = 4;
const DEFAULT_CHIP_WIDTH = 160;
const SUMMARY_CHIP_WIDTH = 40;

export const getVisibleChipCount = ({
  chipWidths,
  containerWidth,
  summaryChipWidth = SUMMARY_CHIP_WIDTH,
}) => {
  if (!chipWidths.length) return 0;

  if (!Number.isFinite(containerWidth) || containerWidth <= 0) {
    return chipWidths.length;
  }

  let visibleCount = 0;
  let usedWidth = 0;

  chipWidths.forEach((chipWidth, index) => {
    const chipSpace = (visibleCount > 0 ? CHIP_GAP : 0) + chipWidth;
    const remainingCount = chipWidths.length - (index + 1);
    const summarySpace = remainingCount > 0 ? CHIP_GAP + summaryChipWidth : 0;

    if (
      visibleCount === 0 ||
      usedWidth + chipSpace + summarySpace <= containerWidth
    ) {
      usedWidth += chipSpace;
      visibleCount += 1;
    }
  });

  return Math.max(1, visibleCount);
};

const MultipleSelect = ({
  id,
  item,
  options,
  selectedOptions,
  handleChange,
  disabled,
}) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [containerWidth, setContainerWidth] = useState(
    Number.POSITIVE_INFINITY
  );
  const [chipWidths, setChipWidths] = useState({});
  const measureRefs = useRef({});
  const selectDisplayId = `${item}-${id}-select`;

  const getSelectedOptions = (selectedValues = []) =>
    options.filter(({ value }) => selectedValues.includes(value));

  const selectedItems = useMemo(
    () => getSelectedOptions(selectedOptions),
    [options, selectedOptions]
  );

  const handleDeleteChip = (selectedValueToRemove) => () => {
    handleChange(
      selectedOptions.filter((value) => value !== selectedValueToRemove)
    );
  };

  const preventChipMouseDown = (event) => {
    event.stopPropagation();
  };

  useLayoutEffect(() => {
    const updateWidth = () => {
      const selectDisplay = document.getElementById(selectDisplayId);

      setContainerWidth(selectDisplay?.clientWidth || Number.POSITIVE_INFINITY);
    };

    updateWidth();

    const selectDisplay = document.getElementById(selectDisplayId);

    if (!selectDisplay || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(selectDisplay);

    return () => resizeObserver.disconnect();
  }, [selectDisplayId]);

  useLayoutEffect(() => {
    const nextChipWidths = selectedItems.reduce((acc, { value }) => {
      const node = measureRefs.current[value];
      acc[value] = node?.getBoundingClientRect?.().width || DEFAULT_CHIP_WIDTH;
      return acc;
    }, {});

    const widthEntries = Object.entries(nextChipWidths);
    const hasChanged =
      widthEntries.length !== Object.keys(chipWidths).length ||
      widthEntries.some(([value, width]) => chipWidths[value] !== width);

    if (hasChanged) {
      setChipWidths(nextChipWidths);
    }
  }, [chipWidths, selectedItems]);

  const renderSelectedValue = (selectedValues) => {
    const selectedItems = getSelectedOptions(selectedValues);
    const visibleChipCount = isOpen
      ? selectedItems.length
      : getVisibleChipCount({
          chipWidths: selectedItems.map(
            ({ value }) => chipWidths[value] || DEFAULT_CHIP_WIDTH
          ),
          containerWidth,
        });
    const visibleItems = selectedItems.slice(0, visibleChipCount);
    const hiddenCount = selectedItems.length - visibleItems.length;

    return (
      <div className={classes.chipsContainer}>
        {visibleItems.map(({ value, name }) => (
          <Tooltip key={value} title={name} position="top" spacing={4}>
            <span
              className={classes.chipWrapper}
              onMouseDown={preventChipMouseDown}
            >
              <Chip
                label={name}
                size="small"
                className={classes.chip}
                classes={{ label: classes.chipLabel }}
                onMouseDown={preventChipMouseDown}
                onDelete={!disabled ? handleDeleteChip(value) : undefined}
                deleteIcon={
                  !disabled ? (
                    <CancelIcon data-testid={`delete-chip-${value}`} />
                  ) : undefined
                }
              />
            </span>
          </Tooltip>
        ))}
        {!isOpen && hiddenCount > 0 && (
          <Chip
            label={`+${hiddenCount}`}
            size="small"
            className={classes.chip}
            classes={{ label: classes.chipLabel }}
          />
        )}
      </div>
    );
  };

  return (
    <FormControl fullWidth>
      <Select
        multiple
        value={selectedOptions}
        id={`${item}-${id}-select`}
        onChange={handleChange}
        disabled={disabled}
        classes={{ root: classes.selectRoot }}
        variant="outlined"
        MenuProps={menuProps}
        SelectDisplayProps={{ 'data-testid': 'multiple-select-trigger' }}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        renderValue={renderSelectedValue}
      >
        {options.map((el) => (
          <MenuItem
            key={el.value}
            value={el.value}
            className={classes.menuItem}
          >
            <Checkbox
              color="primary"
              checked={selectedOptions.includes(el.value)}
              size="small"
            />
            <ListItemText primary={el.name} />
          </MenuItem>
        ))}
      </Select>
      <div className={classes.measureContainer} aria-hidden>
        {selectedItems.map(({ value, name }) => (
          <Chip
            key={value}
            label={name}
            size="small"
            className={classes.chip}
            classes={{ label: classes.chipLabel }}
            ref={(node) => {
              if (node) {
                measureRefs.current[value] = node;
              }
            }}
            onDelete={!disabled ? () => {} : undefined}
            deleteIcon={!disabled ? <CancelIcon /> : undefined}
          />
        ))}
      </div>
    </FormControl>
  );
};

MultipleSelect.propTypes = {
  id: PropTypes.string.isRequired,
  item: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ).isRequired,
  selectedOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  handleChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

MultipleSelect.defaultProps = {
  selectedOptions: [],
  disabled: false,
};

export default MultipleSelect;
